import config from "../config.js";
import httpClient, { authService } from "./httpClient.js";

const API_BASE_URL = config.API_BASE_URL;

// Helper function to handle API endpoints
// Helper function to handle API endpoints
const getEndpoint = (path) => {
  return path.startsWith('http') ? path : `${API_BASE_URL}/${path}`;
};

export const fetchData = async (endpoint, options = {}) => {
  try {
    const url = getEndpoint(endpoint);
    const method = options.method || 'GET';
    const data = options.body ? JSON.parse(options.body) : undefined;
    const headers = options.headers || {};

    let response;
    switch (method.toUpperCase()) {
      case 'GET':
        response = await httpClient.get(url, { headers });
        break;
      case 'POST':
        response = await httpClient.post(url, data, { headers });
        break;
      case 'PUT':
        response = await httpClient.put(url, data, { headers });
        break;
      case 'DELETE':
        response = await httpClient.delete(url, { headers });
        break;
      case 'PATCH':
        response = await httpClient.patch(url, data, { headers });
        break;
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }

    return response;
  } catch (error) {
    console.error("Error in fetchData:", error.message);
    throw error;
  }
};

export const deleteIntegration = async (id) => {
  return fetchData(`api/integrations/delete/${id}/`, {
    method: 'DELETE',
  });
};

export const upsertIntegration = async (id, payload, status) => {
  const updatedPayload = { ...payload, status: status };
  return fetchData(`api/integrations/upsert/${id}/`, {
    method: 'POST',
    body: JSON.stringify(updatedPayload),
  });
};

export const getIntegrations = async (page = 1, page_size = 10, status = 'All', search = '') => {
  return fetchData(`api/integrations?page=${page}&page_size=${page_size}&status=${status}&search=${search}`);
};

export const getIntegrationById = async (id) => {
  return fetchData(`api/integrations/${id}/`);
};

export const getPipelineDetail = async (pipelineId) => {
  return fetchData(`api/integrations/pipelines/${pipelineId}/`);
};

export const deletePipeline = async (pipelineId) => {
  return fetchData(`api/integrations/pipelines/${pipelineId}/delete/`, { method: 'DELETE' });
};

export const signupUser = async (userData) => {
  return fetchData("api/users/signup/", {
    method: 'POST',
    body: JSON.stringify(userData),
  });
};

export const loginUser = async (userData) => {
  try {
    const response = await fetchData("api/users/login/", {
      method: 'POST',
      body: JSON.stringify(userData),
    });

    // Check if login was successful (has access token)
    if (!response || !response.access) {
      throw new Error('Invalid username or password');
    }

    // Store tokens using authService
    if (response.access && response.refresh) {
      authService.setTokens({
        access: response.access,
        refresh: response.refresh
      });
    }

    return response;
  } catch (error) {
    // Re-throw the error so the component can catch it
    throw error;
  }
};

export const verifyEmailToken = async (token) => {
  return fetchData(`api/users/verify-email/${token}/`);
};

/**
 * Fetches the current authenticated user's profile.
 * @returns {Promise<any>} A promise that resolves with the user profile data.
 */
export const getMyProfile = async () => {
  return fetchData("api/users/me/", {
    method: 'GET'
  });
};

export const getRunHistory = async (id, page, page_size) => {
  return fetchData(`api/integrations/runhistory/${id}?page=${page}&&page_size=${page_size}`);
}

export const getLogData = async (id) => {
  return []; // Stub — wire to real endpoint when available
}

export const getRunLogData = async (id) => {
  return fetchData(`api/integrations/runlogdata/${id}`);
}

/**
 * Send a message to the Builder Agent.
 * @param {string} message - The user's message.
 * @param {string|null} sessionId - The current session UUID (null to start a new session).
 */
export const askBuilderAgent = async (message, sessionId) => {
  const payload = { message };
  if (sessionId) {
    payload.session_id = sessionId;
  }
  return fetchData('api/agents/builder/ask/', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

/**
 * Fetch Builder Agent sessions for the sidebar.
 * @param {string} [search] - Optional search query to filter across ALL sessions.
 *                             Omit (or pass empty string) to get the 20 most recent.
 */
export const getAgentSessions = async (search = '') => {
  const path = search.trim()
    ? `api/agents/builder/sessions/?search=${encodeURIComponent(search.trim())}`
    : 'api/agents/builder/sessions/';
  return fetchData(path);
}

/**
 * Fetch the full message history + state for a specific Builder Agent session.
 * @param {string} sessionId - The session UUID.
 */
export const getAgentHistory = async (sessionId) => {
  return fetchData(`api/agents/builder/sessions/${sessionId}/history/`);
}

/**
 * Permanently delete a Builder Agent session and all its messages.
 * @param {string} sessionId - The session UUID.
 */
export const deleteAgentSession = async (sessionId) => {
  return fetchData(`api/agents/builder/sessions/${sessionId}/`, {
    method: 'DELETE',
  });
}

/**
 * Send audio to the Builder Agent voice endpoint (transcribe + agent).
 * @param {Blob} audioBlob — recorded audio blob
 * @param {string|null} sessionId — session UUID to resume (null for new)
 */
export const askBuilderVoice = async (audioBlob, sessionId) => {
  const formData = new FormData();
  formData.append('audio', audioBlob, 'recording.webm');
  if (sessionId) {
    formData.append('session_id', sessionId);
  }
  const url = `${API_BASE_URL}/api/agents/builder/voice/ask/`;
  const response = await httpClient.post(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response;
}

/**
 * Transcribe audio only — returns { transcript } with no agent call.
 * Used for the two-step voice flow: transcribe → show text → send to agent.
 * @param {Blob} audioBlob — recorded audio blob
 */
export const transcribeVoice = async (audioBlob) => {
  const formData = new FormData();
  formData.append('audio', audioBlob, 'recording.webm');
  const url = `${API_BASE_URL}/api/integrations/chatbot/voice/transcribe/`;
  const response = await httpClient.post(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response;
}

// ─────────────────────────────────────────────────────────────────────────────
// BI Agent (Data Analyst mode)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Send a message to the BI Agent, or approve/cancel a pending query.
 * @param {string} message - The user's question (empty string when approving/cancelling).
 * @param {string|null} sessionId - Session UUID to resume (null for new session).
 * @param {boolean} approvePendingQuery - true when the user clicks [Run Query].
 * @param {boolean} cancelPendingQuery  - true when the user clicks [Cancel].
 */
export const askBIAgent = async (message, sessionId, approvePendingQuery = false, cancelPendingQuery = false) => {
  const payload = { message };
  if (sessionId) payload.session_id = sessionId;
  if (approvePendingQuery) payload.approve_pending_query = true;
  if (cancelPendingQuery) payload.cancel_pending_query = true;
  return fetchData('api/agents/pipeline/ask/', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Unified Agent — single endpoint, no mode tabs.
 * The Router LangGraph on the backend classifies intent automatically.
 *
 * @param {string}  message              - User's message.
 * @param {string}  sessionId            - Resume existing session (or '' for new).
 * @param {boolean} approvePendingQuery  - true when user clicks [Run Query].
 * @param {boolean} cancelPendingQuery   - true when user clicks [Cancel].
 * @param {string}  forceRoute           - "builder" | "analyst" — used after
 *                                         the user picks a clarification chip.
 */
export const askAgent = async (
  message,
  sessionId,
  approvePendingQuery = false,
  cancelPendingQuery  = false,
  forceRoute          = '',
  integrationId       = '',   // passed when user edits a diagram — loads existing integration
) => {
  const payload = { message };
  if (sessionId)           payload.session_id            = sessionId;
  if (approvePendingQuery) payload.approve_pending_query = true;
  if (cancelPendingQuery)  payload.cancel_pending_query  = true;
  if (forceRoute)          payload.force_route           = forceRoute;
  if (integrationId)       payload.integration_id        = integrationId;
  return fetchData('api/agents/ask/', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

/**
 * Fetch BI Agent sessions for the sidebar.
 * @param {string} [search] - Optional search query.
 */
export const getBISessions = async (search = '') => {
  const path = search.trim()
    ? `api/agents/bi/sessions/?search=${encodeURIComponent(search.trim())}`
    : 'api/agents/bi/sessions/';
  return fetchData(path);
}

/**
 * Fetch the full message history for a specific BI Agent session.
 * @param {string} sessionId - The session UUID.
 */
export const getBIHistory = async (sessionId) => {
  return fetchData(`api/agents/bi/sessions/${sessionId}/history/`);
}

/**
 * Permanently delete a BI Agent session and all its messages.
 * @param {string} sessionId - The session UUID.
 */
export const deleteBISession = async (sessionId) => {
  return fetchData(`api/agents/bi/sessions/${sessionId}/`, {
    method: 'DELETE',
  });
}
